import React from 'react';
import { LocalizeContextProps, TranslateFunction, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Alert, AlertProps } from 'reactstrap';
import { Dispatch } from 'redux';
import { ErrorCode } from '../../clients/aasServerClient.generated';
import { hideAlert } from '../../stores/alerts/actionCreators';
import { AlertItemState } from '../../stores/alerts/reducer';
import { RootState } from '../../stores/store';

interface AlertsProps extends LocalizeContextProps {
    alerts: AlertItemState[];
}

interface AlertOnHide {
    onHide: (id: string) => void;
}

type AlertItemProps = AlertsProps & AlertItemState & AlertOnHide;

class AlertItem extends React.Component<AlertItemProps> {
    constructor(props: AlertItemProps) {
        super(props);
    }

    public alertFormatter = (t: TranslateFunction, alert: AlertItemState) => {
        const title: string = alert.title;
        let message: JSX.Element;

        if (alert.type === 'error') {
            switch (alert.message.errorCode || alert.message.name) {
                case ErrorCode.AUTH_ERROR:
                    message = (
                        <p>
                            {t('errors.AUTH_ERROR', {}, {
                                onMissingTranslation: () => t('errors.undefined') as string
                            }) as string}
                        </p>
                    );
                    break;
                case ErrorCode.ValidateError:
                    message = (
                        <ul>
                            {
                                Object.keys(alert.message.fields).map((key: string, i: number) => (
                                    <li key={i}>
                                        {`${key.replace('body.', '') + ': '}${t('errors.INPUT_ERROR.' + alert.message.fields[key].message, {}, {
                                            onMissingTranslation:
                                                () => t('errors.INPUT_ERROR.undefined') as string
                                        }) as string}`}
                                    </li>
                                ))}
                        </ul>
                    );
                    break;
                case ErrorCode.INPUT_ERROR:
                    message = (
                        <ul>
                            { // tslint:disable-next-line:no-any
                                alert.message.fields.map((field: any, i: number) => (
                                    <li key={i}>
                                        {`${field.msg.hideField ? '' : field.param + ': '}${t('errors.INPUT_ERROR.' + field.msg.errorCode, {}, {
                                            onMissingTranslation:
                                                () => t('errors.INPUT_ERROR.undefined') as string
                                        }) as string}`}
                                    </li>
                                ))}
                        </ul>
                    );
                    break;
                case ErrorCode.FORBIDDEN:
                case ErrorCode.NOT_FOUND:
                case ErrorCode.SERVER_ERROR:
                case ErrorCode.UNAUTHORIZED:
                    message = (
                        <p>
                            {alert.message.extraMessage
                                ? alert.message.extraMessage
                                : t('errors.' + alert.message.errorCode + '.' + alert.message.message, {}, {
                                    onMissingTranslation: () =>
                                        t('errors.' + alert.message.errorCode + '.undefined') as string
                            })}
                        </p>
                    );
                    break;
                default:
                    message = (
                        <p>
                            {t('errors.undefined') as string}
                        </p>
                    );
                    break;
            }
        } else {
            message = (
                <p>
                    {alert.message}
                </p>
            );
        }

        return { title, message };
    }

    public render() {
        // https://tc39.github.io/ecma262/#sec-array.prototype.findindex
        if (!Array.prototype.findIndex) {
            Object.defineProperty(Array.prototype, 'findIndex', {
            value: function(predicate : any) {
            // 1. Let O be ? ToObject(this value).
                if (this == null) {
                throw new TypeError('"this" is null or not defined');
                }
        
                var o = Object(this);
        
                // 2. Let len be ? ToLength(? Get(O, "length")).
                var len = o.length >>> 0;
        
                // 3. If IsCallable(predicate) is false, throw a TypeError exception.
                if (typeof predicate !== 'function') {
                throw new TypeError('predicate must be a function');
                }
        
                // 4. If thisArg was supplied, let T be thisArg; else let T be undefined.
                var thisArg = arguments[1];
        
                // 5. Let k be 0.
                var k = 0;
        
                // 6. Repeat, while k < len
                while (k < len) {
                // a. Let Pk be ! ToString(k).
                // b. Let kValue be ? Get(O, Pk).
                // c. Let testResult be ToBoolean(? Call(predicate, T, « kValue, k, O »)).
                // d. If testResult is true, return k.
                var kValue = o[k];
                if (predicate.call(thisArg, kValue, k, o)) {
                    return k;
                }
                // e. Increase k by 1.
                k++;
                }
        
                // 7. Return -1.
                return -1;
            },
            configurable: true,
            writable: true
            });
        }

        const t = this.props.translate;
        const { title, message } = this.alertFormatter(t, this.props);
        return (
            <Alert
                key={this.props.id}
                color={this.props.color}
                isOpen={this.props.alerts.findIndex((alert) => alert.id === this.props.id) >= 0}
                toggle={() => this.props.onHide(this.props.id)}
            >
                {this.props.title && <h6 className="alert-heading">{title}</h6>}
                {this.props.title && this.props.message && <hr />}
                {this.props.message && (
                    <React.Fragment>
                        {message}
                    </React.Fragment>
                )}
            </Alert>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    alerts: state.alerts
});

const mapDispatchToProps = (dispatch: Dispatch): AlertOnHide => ({
    onHide: (id: string) => {
        dispatch(hideAlert(id));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(withLocalize(AlertItem));
